import type { CrossCheck } from "../types/payrollAdmin.js";
import {
  AlertTriangle,
  ChevronDown,
  CircleDotDashed,
  Eye,
  OctagonAlert,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { type BalanceCheckKey } from "../components/payroll/BalanceResolutionModal.js";
import { Button, StatusChip } from "../components/ui/index.js";
import {
  StudentAgreementBadge,
  WorkloadBadge,
} from "../components/payroll/Badges.js";
import { PayrollUnitToggle } from "../components/payroll/PeriodPickers.js";
import { OvertimePotEditButton } from "../components/payroll/OvertimePotDialog.js";
import {
  ComparisonHeader,
  ComparisonMetricCell,
  MetricCell,
  approvalIssueValues,
  crossCheckClass,
} from "../components/payroll/MetricCells.js";
import { shouldShowApprovalBlockingLog } from "./payrollApprovalIssues.js";
import { formatDuration } from "./payrollDisplay.js";
import type { ReactNode, MouseEvent } from "react";
import type { BalanceResult, PayslipRecord } from "../types/payrollAdmin.js";
import type { ApprovalBlockingEmployee } from "./payrollApprovalIssues.js";
import type { PayrollDisplayUnit } from "./payrollDisplay.js";
const balanceCheckLabel: Record<BalanceCheckKey, string> = {
  workload: "Payslip worked coverage",
  overtime: "Overtime",
  vacation: "Vacation",
  doctor: "Doctor",
  familyCare: "Family care",
  illness: "Illness",
  publicHoliday: "Public holidays",
  paidLeave: "Paid leave",
  remainingVacation: "Vacation remaining",
};
export type PendingBalanceRow = {
  pending: true;
  id: string;
  profileId: string;
  employeeName: string;
};
export interface BalanceLedgerModel {
  rows: Array<BalanceResult | PendingBalanceRow>;
  totalCount: number;
  hiddenCount: number;
  hideInactive: boolean;
  displayUnit: PayrollDisplayUnit;
  showPaidLeave: boolean;
  monthClosed: boolean;
  blocking: {
    count: number;
    employees: ApprovalBlockingEmployee[];
    inputsReady: boolean;
  };
  calculation: {
    error: string | null;
    approvalError: string | null;
    busy: boolean;
    automaticPending: boolean;
    manualAvailable: boolean;
    recalculationPending: boolean;
    retryResultId: number;
    retryUserId: number;
  };
  rowRefresh: {
    busy: boolean;
    disabled: boolean;
    pendingEmployeeId: string | undefined;
    errorEmployeeId: string | undefined;
    error: string | null;
  };
}
export interface BalanceLedgerActions {
  onResolve: (
    employeeId: number,
    check: BalanceCheckKey,
    trigger?: HTMLButtonElement,
  ) => void;
  onShowResult: (resultId: number) => void;
  onOpenPayslip: (
    record: Pick<PayslipRecord, "id">,
    mode?: "edit" | "read",
  ) => void;
  onEditOvertime: (employeeId: number) => void;
  onRecalculate: () => void;
  onRefreshRow: (employeeId: string) => void;
  onDisplayUnit: (unit: PayrollDisplayUnit) => void;
  onToggleInactive: () => void;
}
export function PayrollBalanceLedger({
  model,
  actions,
}: {
  model: BalanceLedgerModel;
  actions: BalanceLedgerActions;
}) {
  const {
    rows: calculationLedgerRows,
    totalCount,
    hiddenCount: hiddenZeroValueCalculationCount,
    hideInactive: hideZeroValueCalculationRows,
    displayUnit,
    showPaidLeave: showCalculationPaidLeave,
    monthClosed,
    rowRefresh,
  } = model;
  const {
    count: approvalBlockingCheckCount,
    employees: approvalBlockingLog,
    inputsReady: calculationInputsReady,
  } = model.blocking;
  const {
    error: balanceCalculationFailure,
    approvalError,
    busy: calculationBusy,
    automaticPending: automaticBalanceCalculationPending,
    manualAvailable: manualRecalculationAvailable,
    recalculationPending,
    retryResultId: recalculationRetryResultId,
    retryUserId: recalculationRetryUserId,
  } = model.calculation;
  const {
    onResolve: openBalanceResolution,
    onShowResult: showBalanceResult,
    onOpenPayslip: openPayslipSheet,
    onEditOvertime: setOvertimeEditEmployeeId,
    onRecalculate: requestBalanceCalculation,
    onDisplayUnit: setDisplayUnit,
  } = actions;
  const setHideZeroValueCalculationRows = actions.onToggleInactive;
  const rowRefreshControl = (
    employeeId: string,
    name: string,
    reason: string,
  ): ReactNode => (
    <div
      className="payroll-row-refresh"
      title={`Recalculate balances for ${name}`}
    >
      <span className="text-xs font-semibold text-danger" title={reason}>
        Needs recalculation
      </span>
      <Button
        type="button"
        size="md"
        iconOnly
        variant="ghost"
        className="payroll-row-refresh-button"
        loading={rowRefresh.busy && rowRefresh.pendingEmployeeId === employeeId}
        aria-label={`Recalculate balances for ${name}`}
        disabled={rowRefresh.disabled}
        onClick={() => actions.onRefreshRow(employeeId)}
      >
        <RotateCcw size={14} aria-hidden="true" />
      </Button>
      {rowRefresh.errorEmployeeId === employeeId && rowRefresh.error && (
        <p role="alert" className="break-words text-xs text-danger">
          {rowRefresh.error}
        </p>
      )}
    </div>
  );
  return (
    <section className="payroll-balance-ledger min-w-0 pt-3 lg:flex lg:h-full lg:min-h-0 lg:flex-col">
      {shouldShowApprovalBlockingLog({
        activeStageId: "reconcile",
        blockingCheckCount: approvalBlockingCheckCount,
        calculationInputsReady,
      }) && (
        <details className="group mb-3 overflow-hidden rounded-lg border border-danger/35 bg-danger-subtle/20">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-danger-subtle/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-danger">
            <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-foreground">
              <OctagonAlert
                size={16}
                className="shrink-0 text-danger"
                aria-hidden="true"
              />
              {approvalBlockingCheckCount} mismatch
              {approvalBlockingCheckCount === 1 ? "" : "es"} found
            </span>
            <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-danger">
              Review
              <ChevronDown
                size={15}
                className="transition-transform group-open:rotate-180"
                aria-hidden="true"
              />
            </span>
          </summary>
          <div className="border-t border-danger/20 bg-surface/70">
            {approvalBlockingLog.length > 0 ? (
              <ol
                className="max-h-64 divide-y divide-border/70 overflow-y-auto"
                aria-label="Unresolved payroll mismatches"
              >
                {approvalBlockingLog.flatMap((employee) =>
                  employee.issues.map((issue) => {
                    const values = approvalIssueValues(
                      issue,
                      displayUnit,
                      employee.hoursPerDay,
                    );
                    return (
                      <li
                        key={`${employee.userId}-${issue.id}`}
                        className="flex min-w-0 flex-wrap items-center gap-3 px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {employee.employeeName}
                          </p>
                          <p className="truncate text-xs text-foreground-muted">
                            {issue.label}
                            {values ? ` · ${values}` : ""}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="lg"
                          variant="outline"
                          className="shrink-0"
                          aria-label={`Review ${issue.label} for ${employee.employeeName}`}
                          onClick={() => {
                            if (issue.reviewTarget.kind === "resolution") {
                              openBalanceResolution(
                                employee.userId,
                                issue.reviewTarget.checkKey,
                              );
                              return;
                            }
                            showBalanceResult(issue.reviewTarget.resultId);
                          }}
                        >
                          <Eye size={16} aria-hidden="true" />
                          Review
                        </Button>
                      </li>
                    );
                  }),
                )}
              </ol>
            ) : (
              <p className="px-3 py-2 text-sm text-warning">
                Mismatch details are unavailable. Recalculate to refresh them.
              </p>
            )}
          </div>
        </details>
      )}
      <div id="calculation-details" className="scroll-mt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-foreground">
            Calculation details
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground-muted">
              Time display
            </span>
            <PayrollUnitToggle value={displayUnit} onChange={setDisplayUnit} />
          </div>
        </div>
      </div>

      {(balanceCalculationFailure || Boolean(approvalError)) && (
        <p className="mt-2 text-sm text-danger">
          {balanceCalculationFailure ??
            approvalError ??
            "Balance action failed."}
        </p>
      )}

      {calculationLedgerRows.length ? (
        <>
          <div
            aria-label="Scrollable calculation details"
            tabIndex={0}
            className="payroll-balance-ledger__scroll mt-3 hidden h-[calc(100dvh-18rem)] overflow-auto overscroll-x-contain overscroll-y-auto rounded-xl border border-border bg-surface [scrollbar-gutter:stable] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent md:block lg:h-auto lg:min-h-0 lg:flex-1"
          >
            <table
              className={`payroll-balance-ledger__table ${showCalculationPaidLeave ? "min-w-[81rem]" : "min-w-[74rem]"} w-full table-fixed text-left text-xs`}
            >
              <colgroup>
                <col className="w-48" />
                <col className="w-24" />
                <col className="w-32" />
                <col className="w-28" />
                <col className="w-24" />
                {Array.from(
                  { length: showCalculationPaidLeave ? 6 : 5 },
                  (_, index) => (
                    <col key={index} className="w-28" />
                  ),
                )}
              </colgroup>
              <thead className="sticky top-0 z-20 bg-background text-foreground-muted">
                <tr className="border-b border-border/70 text-[10px] uppercase tracking-[0.12em] text-foreground-subtle">
                  <th
                    scope="colgroup"
                    colSpan={5}
                    className="px-3 py-1.5 text-left font-semibold"
                  >
                    Payroll balances
                  </th>
                  <th
                    scope="colgroup"
                    colSpan={showCalculationPaidLeave ? 6 : 5}
                    className="border-l-2 border-border bg-elevated/40 px-3 py-1.5 text-left font-semibold"
                  >
                    Leave &amp; absence
                  </th>
                </tr>
                <tr>
                  <th className="sticky left-0 z-30 w-48 min-w-48 bg-background px-2 py-2 font-medium">
                    Employee
                  </th>
                  {[
                    {
                      label: "Timesheet total",
                      title:
                        "All recorded timesheet hours, including work and absence entries.",
                    },
                    {
                      label: "Payslip worked",
                      title:
                        "Odpracované — worked hours extracted from the payslip.",
                    },
                    {
                      label: "Contract hours",
                      title:
                        "Úväzok — nominal contract hours extracted from the payslip. Overtime uses this value.",
                    },
                    {
                      label: "Overtime",
                      title:
                        "Timesheet total minus effective comparison hours: contract hours for ordinary employees, twice payslip worked for student agreements.",
                    },
                  ].map(({ label, title }) => (
                    <th
                      key={label}
                      title={title}
                      className="break-words px-2 py-2 align-bottom font-medium leading-4"
                    >
                      {label}
                    </th>
                  ))}
                  <ComparisonHeader
                    label="Vacation"
                    className="border-l-2 border-border bg-elevated/40"
                  />
                  <ComparisonHeader label="Doctor (source)" />
                  <ComparisonHeader label="Family care" />
                  <ComparisonHeader label="Illness" />
                  <ComparisonHeader label="Public holidays" />
                  {showCalculationPaidLeave && (
                    <ComparisonHeader label="Paid leave (legacy)" />
                  )}
                </tr>
              </thead>
              <tbody>
                {calculationLedgerRows.map((result) =>
                  "pending" in result ? (
                    <tr
                      key={result.id}
                      data-balance-employee-id={result.profileId}
                      className="payroll-balance-row--stale border-t border-border"
                    >
                      <th
                        scope="row"
                        className="sticky left-0 z-10 px-2 py-2 text-left"
                      >
                        <div className="payroll-balance-identity">
                          <span className="break-words text-sm font-semibold">
                            {result.employeeName}
                          </span>
                          {rowRefreshControl(
                            result.profileId,
                            result.employeeName,
                            "New eligible Work Profile",
                          )}
                        </div>
                      </th>
                      <td
                        colSpan={showCalculationPaidLeave ? 10 : 9}
                        className="px-3 py-2 text-sm text-foreground-muted"
                      >
                        No calculation yet for this employee.
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={result.id}
                      data-balance-result-id={result.id}
                      tabIndex={-1}
                      data-recalculation-required={
                        result.recalculationRequired || undefined
                      }
                      className={`border-t border-border text-foreground ${result.recalculationRequired ? "payroll-balance-row--stale" : ""}`}
                    >
                      <td className="sticky left-0 z-10 w-48 min-w-48 bg-surface px-2 py-1.5 align-top">
                        <div className="payroll-balance-identity min-w-0">
                          {result.payslipRecordId ? (
                            <button
                              type="button"
                              onClick={() =>
                                openPayslipSheet(
                                  {
                                    id: result.payslipRecordId!,
                                  },
                                  "read",
                                )
                              }
                              aria-label={`Open payslip & values for ${result.employeeName}`}
                              title={`Open payslip & values for ${result.employeeName}`}
                              className="inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-md text-left text-sm font-semibold leading-5 text-foreground hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                              <span className="break-words">
                                {result.employeeName}
                              </span>
                              <Eye
                                size={14}
                                className="shrink-0 text-foreground-muted"
                                aria-hidden="true"
                              />
                            </button>
                          ) : (
                            <p className="break-words text-sm font-semibold leading-5">
                              {result.employeeName}
                            </p>
                          )}
                          <div className="mt-0.5 flex flex-wrap items-center gap-1 empty:hidden">
                            <WorkloadBadge
                              percent={result.payrollWorkloadPercent ?? null}
                              student={
                                result.payrollContractType ===
                                "student_agreement"
                              }
                            />
                            {result.studentAgreementAdjustment && (
                              <StudentAgreementBadge />
                            )}
                            {recalculationPending &&
                              (result.id === recalculationRetryResultId ||
                                result.userId === recalculationRetryUserId) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  type="button"
                                  onClick={() => requestBalanceCalculation()}
                                  disabled={calculationBusy}
                                  className="inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-warning/40 px-2.5 text-xs font-semibold text-warning hover:bg-warning-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
                                >
                                  <RotateCcw size={13} aria-hidden="true" />
                                  Retry check
                                </Button>
                              )}
                          </div>
                          {result.recalculationRequired &&
                            rowRefreshControl(
                              result.profileId,
                              result.employeeName,
                              result.recalculationReason ||
                                "Work Profile changed",
                            )}
                        </div>
                      </td>
                      <MetricCell>
                        {result.timesheetVersionNumber
                          ? formatDuration(
                              result.timesheetHours,
                              displayUnit,
                              result.hoursPerDay,
                            )
                          : "Missing"}
                      </MetricCell>
                      <MetricCell
                        check={
                          result.crossChecks?.["workload"]?.state === "red" ||
                          result.crossChecks?.["workload"]?.accepted === true
                            ? result.crossChecks["workload"]
                            : undefined
                        }
                        title={result.crossChecks?.["workload"]?.message}
                        onResolve={(trigger) =>
                          openBalanceResolution(
                            result.userId,
                            "workload",
                            trigger,
                          )
                        }
                        resolveLabel={`Review Payslip worked coverage for ${result.employeeName}`}
                      >
                        {result.payslipPaidHours === null
                          ? result.payslipRecordId
                            ? "Paid hours not parsed"
                            : "Missing matched payslip"
                          : formatDuration(
                              result.payslipPaidHours,
                              displayUnit,
                              result.hoursPerDay,
                            )}
                      </MetricCell>
                      <MetricCell>
                        {result.payslipNominalHours === null
                          ? result.payslipRecordId
                            ? "Nominal hours not parsed"
                            : "Missing matched payslip"
                          : formatDuration(
                              result.payslipNominalHours,
                              displayUnit,
                              result.hoursPerDay,
                            )}
                      </MetricCell>
                      <td className="px-2 py-1 whitespace-nowrap">
                        <OvertimePotEditButton
                          balanceHours={result.closingOvertimeHours}
                          value={formatDuration(
                            result.closingOvertimeHours,
                            displayUnit,
                            result.hoursPerDay,
                          )}
                          monthlyDeltaValue={formatDuration(
                            result.overtimeDeltaHours,
                            displayUnit,
                            result.hoursPerDay,
                          )}
                          employeeName={result.employeeName}
                          disabled={monthClosed}
                          onClick={() =>
                            setOvertimeEditEmployeeId(result.userId)
                          }
                        />
                      </td>
                      <ComparisonMetricCell
                        check={result.crossChecks?.["vacation"] as CrossCheck}
                        fallbackLeftHours={result.vacationHours}
                        unit={displayUnit}
                        hoursPerDay={result.hoursPerDay}
                        className="border-l-2 border-border bg-elevated/20"
                        onResolve={() =>
                          openBalanceResolution(result.userId, "vacation")
                        }
                        resolveLabel={
                          result.crossChecks?.["vacation"]?.accepted
                            ? `View accepted vacation evidence for ${result.employeeName}`
                            : `Resolve vacation difference for ${result.employeeName}`
                        }
                      />
                      <ComparisonMetricCell
                        check={result.crossChecks?.["doctor"] as CrossCheck}
                        fallbackLeftHours={result.doctorHours}
                        unit={displayUnit}
                        hoursPerDay={result.hoursPerDay}
                        onResolve={() =>
                          openBalanceResolution(result.userId, "doctor")
                        }
                        resolveLabel={`Resolve doctor absence difference for ${result.employeeName}`}
                      />
                      <ComparisonMetricCell
                        check={result.crossChecks?.["familyCare"] as CrossCheck}
                        fallbackLeftHours={result.familyCareHours ?? 0}
                        unit={displayUnit}
                        hoursPerDay={result.hoursPerDay}
                        onResolve={() =>
                          openBalanceResolution(result.userId, "familyCare")
                        }
                        resolveLabel={`Resolve family care difference for ${result.employeeName}`}
                      />
                      <ComparisonMetricCell
                        check={result.crossChecks?.["illness"] as CrossCheck}
                        fallbackLeftHours={result.illnessHours ?? 0}
                        unit={displayUnit}
                        hoursPerDay={result.hoursPerDay}
                        onResolve={() =>
                          openBalanceResolution(result.userId, "illness")
                        }
                        resolveLabel={`Resolve illness difference for ${result.employeeName}`}
                      />
                      <ComparisonMetricCell
                        check={
                          result.crossChecks?.["publicHoliday"] as CrossCheck
                        }
                        fallbackLeftHours={result.publicHolidayHours ?? 0}
                        unit={displayUnit}
                        hoursPerDay={result.hoursPerDay}
                        onResolve={() =>
                          openBalanceResolution(result.userId, "publicHoliday")
                        }
                        resolveLabel={`Resolve public holiday difference for ${result.employeeName}`}
                      />
                      {showCalculationPaidLeave && (
                        <ComparisonMetricCell
                          check={
                            result.crossChecks?.["paidLeave"] as CrossCheck
                          }
                          fallbackLeftHours={
                            result.crossChecks?.["paidLeave"]
                              ? (result.paidLeaveHours ?? 0)
                              : null
                          }
                          unit={displayUnit}
                          hoursPerDay={result.hoursPerDay}
                          onResolve={() =>
                            openBalanceResolution(result.userId, "paidLeave")
                          }
                          resolveLabel={`Resolve paid leave difference for ${result.employeeName}`}
                        />
                      )}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 grid gap-3 md:hidden">
            {calculationLedgerRows.map((result) => {
              if ("pending" in result)
                return (
                  <article
                    key={result.id}
                    data-balance-employee-id={result.profileId}
                    className="payroll-balance-row--stale rounded-xl border border-border p-4"
                  >
                    <div className="payroll-balance-identity">
                      <h3 className="text-sm font-semibold">
                        {result.employeeName}
                      </h3>
                      {rowRefreshControl(
                        result.profileId,
                        result.employeeName,
                        "New eligible Work Profile",
                      )}
                    </div>
                    <p className="mt-2 text-sm">
                      No calculation yet for this employee.
                    </p>
                  </article>
                );
              const unresolved = Object.entries(
                result.crossChecks ?? {},
              ).filter(
                (entry): entry is [BalanceCheckKey, CrossCheck] =>
                  entry[1]?.state === "red" &&
                  entry[1]?.accepted !== true &&
                  [
                    "vacation",
                    "doctor",
                    "familyCare",
                    "illness",
                    "publicHoliday",
                    "paidLeave",
                  ].includes(entry[0]),
              );
              const accepted = Object.entries(result.crossChecks ?? {}).filter(
                (entry): entry is [BalanceCheckKey, CrossCheck] =>
                  entry[1]?.accepted === true &&
                  [
                    "vacation",
                    "doctor",
                    "familyCare",
                    "illness",
                    "publicHoliday",
                    "paidLeave",
                  ].includes(entry[0]),
              );
              const workloadCheck = result.crossChecks?.["workload"];
              const workloadUnresolved =
                workloadCheck?.state === "red" &&
                workloadCheck.accepted !== true;
              const workloadAccepted = workloadCheck?.accepted === true;
              const reviewCount =
                unresolved.length + (workloadUnresolved ? 1 : 0);
              return (
                <article
                  key={result.id}
                  data-balance-result-id={result.id}
                  tabIndex={-1}
                  data-recalculation-required={
                    result.recalculationRequired || undefined
                  }
                  className={`rounded-xl border border-border bg-surface p-4 ${result.recalculationRequired ? "payroll-balance-row--stale" : ""}`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="payroll-balance-identity min-w-0">
                      <h3 className="text-sm font-semibold text-foreground">
                        {result.payslipRecordId ? (
                          <button
                            type="button"
                            onClick={() =>
                              openPayslipSheet(
                                { id: result.payslipRecordId! },
                                "read",
                              )
                            }
                            aria-label={`Open payslip & values for ${result.employeeName}`}
                            title={`Open payslip & values for ${result.employeeName}`}
                            className="inline-flex min-h-11 max-w-full items-center gap-2 rounded-md text-left hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            <span className="break-words">
                              {result.employeeName}
                            </span>
                            <Eye
                              size={16}
                              className="shrink-0 text-foreground-muted"
                              aria-hidden="true"
                            />
                          </button>
                        ) : (
                          result.employeeName
                        )}
                      </h3>
                      {result.recalculationRequired &&
                        rowRefreshControl(
                          result.profileId,
                          result.employeeName,
                          result.recalculationReason || "Work Profile changed",
                        )}
                      <dl className="col-span-2 mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-foreground-muted">
                        <div>
                          <dt>Timesheet total</dt>
                          <dd className="font-semibold text-foreground">
                            {formatDuration(
                              result.timesheetHours,
                              displayUnit,
                              result.hoursPerDay,
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt>Payslip worked</dt>
                          <dd className="mt-0.5 font-semibold text-foreground">
                            {workloadUnresolved || workloadAccepted ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={(
                                  event: MouseEvent<HTMLButtonElement>,
                                ) =>
                                  openBalanceResolution(
                                    result.userId,
                                    "workload",
                                    event.currentTarget,
                                  )
                                }
                                aria-label={`${workloadAccepted ? "View accepted" : "Review"} Payslip worked coverage for ${result.employeeName}`}
                                className={`${crossCheckClass(workloadCheck)} payroll-reconciliation-target inline-flex min-h-11 items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
                              >
                                {workloadAccepted ? (
                                  <ShieldCheck size={13} aria-hidden="true" />
                                ) : (
                                  <AlertTriangle size={13} aria-hidden="true" />
                                )}
                                {result.payslipPaidHours == null
                                  ? "Missing"
                                  : formatDuration(
                                      result.payslipPaidHours,
                                      displayUnit,
                                      result.hoursPerDay,
                                    )}
                              </Button>
                            ) : result.payslipPaidHours == null ? (
                              "Missing"
                            ) : (
                              formatDuration(
                                result.payslipPaidHours,
                                displayUnit,
                                result.hoursPerDay,
                              )
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt>Contract hours</dt>
                          <dd className="font-semibold text-foreground">
                            {result.payslipNominalHours == null
                              ? "Missing"
                              : formatDuration(
                                  result.payslipNominalHours,
                                  displayUnit,
                                  result.hoursPerDay,
                                )}
                          </dd>
                        </div>
                        <div>
                          <dt>Overtime</dt>
                          <dd className="mt-0.5 font-semibold">
                            <OvertimePotEditButton
                              balanceHours={result.closingOvertimeHours}
                              value={formatDuration(
                                result.closingOvertimeHours,
                                displayUnit,
                                result.hoursPerDay,
                              )}
                              monthlyDeltaValue={formatDuration(
                                result.overtimeDeltaHours,
                                displayUnit,
                                result.hoursPerDay,
                              )}
                              employeeName={result.employeeName}
                              disabled={monthClosed}
                              onClick={() =>
                                setOvertimeEditEmployeeId(result.userId)
                              }
                            />
                          </dd>
                        </div>
                      </dl>
                    </div>
                    <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
                      {!result.recalculationRequired && (
                        <StatusChip
                          tone={reviewCount ? "danger" : "success"}
                          variant="outline"
                        >
                          {result.approved
                            ? "Approved"
                            : reviewCount
                              ? `${reviewCount} to resolve`
                              : "Ready"}
                        </StatusChip>
                      )}
                    </div>
                  </div>
                  {unresolved.length > 0 && (
                    <div className="mt-3 grid gap-2">
                      {unresolved.map(([key, check]) => (
                        <Button
                          variant="ghost"
                          size="sm"
                          key={key}
                          type="button"
                          onClick={() =>
                            openBalanceResolution(result.userId, key)
                          }
                          className="flex min-h-11 items-center justify-between gap-3 rounded-lg border border-danger/35 bg-danger-subtle px-3 text-left text-sm font-semibold text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger"
                        >
                          <span className="flex items-center gap-2">
                            <AlertTriangle size={15} aria-hidden="true" />
                            {balanceCheckLabel[key]}
                          </span>
                          <span className="font-mono text-xs">
                            {check.difference == null &&
                            check.rawDifference == null
                              ? "Needs decision"
                              : formatDuration(
                                  Math.abs(
                                    check.difference ??
                                      check.rawDifference ??
                                      0,
                                  ),
                                  displayUnit,
                                  result.hoursPerDay,
                                )}
                          </span>
                        </Button>
                      ))}
                    </div>
                  )}
                  {accepted.length > 0 && (
                    <div className="mt-3 grid gap-2">
                      {accepted.map(([key]) => (
                        <Button
                          variant="ghost"
                          size="sm"
                          key={key}
                          type="button"
                          onClick={() =>
                            openBalanceResolution(result.userId, key)
                          }
                          aria-label={`View accepted ${balanceCheckLabel[key].toLowerCase()} evidence for ${result.employeeName}`}
                          className="flex min-h-11 items-center justify-between gap-3 rounded-lg border border-warning/35 bg-warning-subtle px-3 text-left text-sm font-semibold text-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
                        >
                          <span className="flex items-center gap-2">
                            <ShieldCheck size={15} aria-hidden="true" />
                            Accepted {balanceCheckLabel[key]}
                          </span>
                          <span className="text-xs">View evidence</span>
                        </Button>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </>
      ) : totalCount > 0 && hideZeroValueCalculationRows ? (
        <p className="mt-3 rounded-xl border border-border bg-surface px-3 py-4 text-center text-sm text-foreground-subtle">
          All {totalCount} employee
          {totalCount === 1 ? " is" : "s are"} hidden because they have no
          matched payslip or payroll activity to review.
        </p>
      ) : (
        <div className="mt-3 rounded-xl border border-border bg-surface px-4 py-5 text-center">
          {automaticBalanceCalculationPending ? (
            <p
              role="status"
              aria-live="polite"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground"
            >
              <CircleDotDashed
                size={16}
                className="animate-spin text-accent"
                aria-hidden="true"
              />
              The first balance calculation started automatically. Checking
              current sources…
            </p>
          ) : (
            <p className="text-sm text-foreground-muted">
              {balanceCalculationFailure
                ? "The automatic balance calculation failed. Review the error above, then try again."
                : "The first balance calculation will start automatically when every prerequisite is ready."}
            </p>
          )}
          {manualRecalculationAvailable && (
            <Button
              type="button"
              className="mt-4"
              onClick={requestBalanceCalculation}
              disabled={calculationBusy || rowRefresh.busy}
            >
              <RotateCcw size={16} aria-hidden="true" />
              {calculationBusy
                ? "Calculating…"
                : balanceCalculationFailure
                  ? "Retry calculation"
                  : "Recalculate balances"}
            </Button>
          )}
        </div>
      )}
      {totalCount > 0 &&
        (!hideZeroValueCalculationRows ||
          hiddenZeroValueCalculationCount > 0) && (
          <div className="mt-2 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setHideZeroValueCalculationRows()}
              className="min-h-8 cursor-pointer px-1 text-xs font-medium text-foreground-subtle opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {hideZeroValueCalculationRows
                ? `Show ${hiddenZeroValueCalculationCount} inactive employee${hiddenZeroValueCalculationCount === 1 ? "" : "s"}`
                : "Hide inactive employees"}
            </Button>
          </div>
        )}
    </section>
  );
}
